/**
 * Hook surface for the holder-side Deposit Protection flow.
 *
 *   - `useDepositProtectionStatus(address)` — fetches the current
 *     server-side delegation state via `GET /api/protection/status`.
 *
 *   - `useEnableDepositProtection()` — requests an ERC-7715 grant
 *     (`wallet_requestExecutionPermissions`) from MetaMask and POSTs
 *     the resulting `permissionContext` blobs to
 *     `/api/protection/enable`. The flow surfaces the wallet
 *     capability check so the caller can route a non-MetaMask user to
 *     the "MetaMask only (for now)" explainer. Enable is
 *     MetaMask-7715-only; the retired 7710-direct enable rail only
 *     lingers as a redemption discriminator for legacy rows.
 *
 *   - `useDisableDepositProtection()` / `useRevokeDepositProtection()`
 *     — `POST /api/protection/{disable,revoke}` mutations.
 *
 * The hooks are split so the `DepositProtectionInfoDialog` (rendered
 * from the profile dropdown) can render status synchronously and only
 * spin up the signing pipeline once the user actually clicks Enable.
 */

'use client';

import { useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type Address, type Hex, getAddress } from 'viem';
import { useWallet } from './use-wallet';
import { useWalletCapabilities } from './use-eip7702';
import { useGrantProtectionPermissions } from './use-grant-protection-permissions';
import {
  useRevokeProtectionGrants,
  pickFullyRevokedIds,
  markGrantsClearedOnServer,
  type RevokableGrant,
} from './use-revoke-protection-grants';

interface ProtectionStatus {
  status: 'off' | 'active';
  delegationId?: number;
  expiresAt?: number;
  weeklyUsdcCap?: string;
  weeklyTakeoverCap?: string;
  allowedUgms?: Address[];
  /**
   * Subset of `allowedUgms` the holder ticked in the picker. Pre-picker
   * rows backfill this to equal `allowedUgms`, so consumers can read
   * it as the canonical "what the holder asked us to protect" set.
   */
  protectedUgms?: Address[];
  /** User-signed runway gate (seconds). */
  runwayGateSeconds?: number;
  /** User-signed runway target (seconds). */
  targetRunwaySeconds?: number;
}

const STATUS_QUERY_KEY = (address?: string | null) => ['deposit-protection-status', address?.toLowerCase()];
const HISTORY_QUERY_KEY = (address?: string | null) => ['deposit-protection-history', address?.toLowerCase()];

export interface ProtectionHistoryItem {
  id: number;
  ugm: Address;
  gridId: string;
  seatId: number;
  taxToken: Address;
  /** Stringified token-units (USDC = 6dp, TAKEOVER = 18dp). */
  principal: string;
  fee: string;
  executedTx: Hex | null;
  /** Millisecond unix timestamp. Null only on rare in-flight rows. */
  executedAt: number | null;
}

interface ProtectionHistoryResponse {
  holder: Address;
  items: ProtectionHistoryItem[];
}

/**
 * Fetch the holder's recent successful top-ups for the
 * Deposit-Protection history view inside the modal. Mirrors
 * `useDepositProtectionStatus` shape; the query stays mounted whether
 * or not the user has protection on (the status check is the
 * downstream gate that decides whether to render the section).
 */
export function useProtectionHistory(address: Address | null | undefined, limit: number = 10): {
  data: ProtectionHistoryResponse | undefined;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: [...HISTORY_QUERY_KEY(address), limit],
    enabled: !!address,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetch(`/api/protection/history?address=${address}&limit=${limit}`);
      if (!res.ok) throw new Error(`history ${res.status}`);
      return (await res.json()) as ProtectionHistoryResponse;
    },
  });
  return { data: query.data, isLoading: query.isLoading };
}

export function useDepositProtectionStatus(address: Address | null | undefined): {
  data: ProtectionStatus | undefined;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
} {
  const query = useQuery({
    queryKey: STATUS_QUERY_KEY(address),
    enabled: !!address,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    queryFn: async () => {
      const res = await fetch(`/api/protection/status?address=${address}`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const json = (await res.json()) as ProtectionStatus;
      return json;
    },
  });
  return { data: query.data, isLoading: query.isLoading, refetch: query.refetch };
}

interface EnableInput {
  weeklyUsdcCap: bigint;
  weeklyTakeoverCap: bigint;
  /** Trigger threshold in seconds — defaults to 72h. Bounded by
   *  `PROTECTION_BOUNDS` in the SDK. */
  runwayGateSeconds: number;
  /** Target runway-after in seconds — defaults to 7d. Must exceed
   *  `runwayGateSeconds`. */
  targetRunwaySeconds: number;
  /** Unix seconds. Defaults to +365 days. ERC-7715 caps at 1 year by
   *  spec (see `grant-permissions.ts` `oneYearFromNow` check), which is
   *  why the default is anchored there. */
  expiresAt?: number;
  /** Allowlisted UGMs. Defaults to a single env-configured UGM. */
  allowedUgms?: Address[];
  /**
   * Subset of `allowedUgms` (or `env.defaultAllowedUgms`) the holder
   * picked in the dialog's per-board picker. When omitted, every
   * allowed UGM is considered protected — matching the legacy /
   * pre-picker behaviour and what the server backfilled for older
   * rows. The dialog always passes this once the picker mounts; the
   * field stays optional so test callers and the future SDK surface
   * don't need to change.
   */
  protectedUgms?: Address[];
}

interface ProtectionEnvAddresses {
  delegationManager: Address;
  depositProtectionEnforcer: Address;
  erc20PeriodicAllowanceEnforcer: Address;
  timestampEnforcer: Address;
  usdc: Address;
  takeover: Address;
  defaultAllowedUgms: Address[];
  chainId: number;
  /**
   * `ProtectionRedeemer` contract address — the onchain delegate the
   * ERC-7715 (MetaMask) enable path grants permissions TO. Replaces the
   * earlier `protectionKeeperAddress` EOA approach: a real contract
   * lets us validate the redemption batch onchain (seat owner, UGM
   * allowlist, runway) before forwarding to the DM.
   *
   * Enable is MetaMask-7715-only, so this MUST be set for any env where
   * holders can turn protection on. The mutation throws a clean error
   * early if it lands on the enable path with this env unset.
   */
  protectionRedeemerAddress: Address | null;
}

/**
 * Read every protection-flow env var via *literal* `process.env.X` access.
 *
 * Next.js (webpack `DefinePlugin`) only inlines `NEXT_PUBLIC_*` env vars
 * into the client bundle when the property access is statically
 * analysable. A previous version of this helper read each value through
 * a local `get = (k) => process.env[k]` indirection, which preserves the
 * bracket access at build time — the bundle then evaluates
 * `process.env[k]` in the browser, where `process.env` is `{}`, and
 * every var resolves to `undefined`. The user-visible symptom was the
 * "no allowed UGMs configured" toast at click-time on staging because
 * `defaultAllowedUgms` was always `[]`; less visibly, the enforcer /
 * delegation-manager / token addresses were all `0x0000…0000`, which
 * would have failed signature verification server-side anyway.
 *
 * Every reference below has to be a literal `process.env.<KEY>` for
 * the inliner to substitute it. Don't refactor through a helper.
 */
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/**
 * Parse a comma-separated list of EIP-55 / lowercased addresses into
 * a canonical `Address[]`. Empty / whitespace-only entries are dropped;
 * each surviving entry is fed through viem's `getAddress` for checksum
 * normalisation.
 *
 * Footgun this exists to avoid: `Array.prototype.map` invokes its
 * callback as `(item, index, array)`, and viem's
 * `getAddress(address, chainId?)` treats the second arg as a chain id
 * (EIP-1191). Writing `.map(getAddress)` therefore re-checksums every
 * entry past index 0 against an off-by-one EIP-1191 chain id, e.g.
 *   - index 1 → EIP-1191 chainId 1 (Ethereum mainnet casing)
 *   - index 2 → EIP-1191 chainId 2 (random; no canonical EIP-1191
 *     consumer exists for chainId=2, but the casing is still wrong)
 * The mis-cased addresses then fail viem's strict EIP-55 check when
 * re-encoded as `address` via `encodeAbiParameters`, surfacing as a
 * cryptic "Address must match its checksum counterpart" at signing
 * time. The arrow wrapper below ensures only the first arg is passed.
 *
 * Exported so the regression test can pin both branches.
 */
export function parseAllowedUgmsList(raw: string | undefined | null): Address[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) => getAddress(s));
}

/**
 * Default expiry for fresh Deposit-Protection delegations, in unix
 * seconds. Anchored at +365 days because MetaMask's ERC-7715
 * implementation hard-caps the permission expiry at 1 year (see
 * the `oneYearFromNow` validator in `grant-permissions.ts`), giving the
 * holder-side modal a consistent expiry countdown.
 *
 * Exported (rather than inlined as a literal at the call site) so the
 * unit test can assert the value without having to spin up a full
 * mutation harness with wallet + EIP-712 mocks.
 *
 * `nowSec` is parameterised purely for deterministic testing — in
 * production it always reads wall-clock.
 */
export function defaultExpiresAtSec(
  nowSec: number = Math.floor(Date.now() / 1000),
): number {
  return nowSec + 365 * 86400;
}

function readEnv(): ProtectionEnvAddresses {
  const redeemerRaw = process.env.NEXT_PUBLIC_PROTECTION_REDEEMER_ADDRESS ?? '';
  return {
    delegationManager: getAddress(
      process.env.NEXT_PUBLIC_DELEGATION_MANAGER ?? ZERO_ADDRESS,
    ),
    depositProtectionEnforcer: getAddress(
      process.env.NEXT_PUBLIC_DEPOSIT_PROTECTION_ENFORCER ?? ZERO_ADDRESS,
    ),
    erc20PeriodicAllowanceEnforcer: getAddress(
      process.env.NEXT_PUBLIC_ERC20_PERIODIC_ALLOWANCE_ENFORCER ?? ZERO_ADDRESS,
    ),
    timestampEnforcer: getAddress(
      process.env.NEXT_PUBLIC_TIMESTAMP_ENFORCER ?? ZERO_ADDRESS,
    ),
    usdc: getAddress(
      process.env.NEXT_PUBLIC_USDC_ADDRESS ?? ZERO_ADDRESS,
    ),
    takeover: getAddress(
      process.env.NEXT_PUBLIC_TAKEOVER_TOKEN_ADDRESS ?? ZERO_ADDRESS,
    ),
    // `parseAllowedUgmsList` is a pure helper — it never reads
    // `process.env`, so the Next.js inliner constraint described above
    // doesn't apply to the call here. The `process.env.<KEY>` read
    // itself stays inline so Webpack can substitute it at build time.
    defaultAllowedUgms: parseAllowedUgmsList(
      process.env.NEXT_PUBLIC_PROTECTION_ALLOWED_UGMS,
    ),
    chainId: Number(process.env.NEXT_PUBLIC_DEPLOYED_CHAIN_ID ?? '8453'),
    // Parsed lazily: an env without the redeemer deployed yet stays
    // `null` rather than throwing at module load. The enable mutation
    // throws a clear error early if a holder tries to turn protection
    // on while this is unset (see `enableVia7715` below).
    protectionRedeemerAddress:
      redeemerRaw && redeemerRaw.startsWith('0x') ? getAddress(redeemerRaw) : null,
  };
}

export function useEnableDepositProtection(): {
  enable: (input: EnableInput) => Promise<void>;
  isPending: boolean;
  capability: ReturnType<typeof useWalletCapabilities>;
} {
  const { address } = useWallet();
  const queryClient = useQueryClient();
  const { grant: grantPermissions } = useGrantProtectionPermissions();
  const { revokeContexts } = useRevokeProtectionGrants();
  const env = readEnv();
  const capability = useWalletCapabilities(env.chainId);

  // 7715 grants captured at the START of an enable, BEFORE the new grant
  // is minted + the old row rotated to `revoked`. `onSuccess` best-effort
  // revokes them so MetaMask's spend-permission list stops accumulating a
  // stale entry per re-enable, and stamps the fully-revoked rows server-
  // side so the holder's superseded count stays accurate.
  const priorGrantsRef = useRef<RevokableGrant[]>([]);

  const mutation = useMutation({
    mutationFn: async (input: EnableInput) => {
      if (!address) throw new Error('No connected wallet');
      if (capability.result?.signingPath !== '7715-grant') {
        throw new Error('Land Protection is currently available with MetaMask only.');
      }
      const expiresAt = input.expiresAt ?? defaultExpiresAtSec();
      const allowedUgms = input.allowedUgms?.length
        ? input.allowedUgms
        : env.defaultAllowedUgms;
      if (allowedUgms.length === 0) {
        throw new Error('Configuration error: no allowed UGMs configured');
      }
      // The picker hands us a subset; clamp it back to `allowedUgms`
      // so a stale UI state can't smuggle in an extra UGM the
      // enforcer wouldn't accept. When omitted (legacy callers /
      // test fixtures), default to the full allowed set so behaviour
      // is unchanged.
      const allowedSet = new Set(allowedUgms.map((a) => a.toLowerCase()));
      const protectedUgms = (input.protectedUgms ?? allowedUgms).filter((a) =>
        allowedSet.has(a.toLowerCase()),
      );
      if (protectedUgms.length === 0) {
        throw new Error('Pick at least one board to protect');
      }

      // Land Protection is MetaMask-7715-only: the guard above already
      // proved `signingPath === '7715-grant'`, so the grant path is the
      // only enable rail. We POST the resulting `permissionContext`
      // blobs to `/api/protection/enable`, which validates + persists
      // the row stamped `signing_path = '7715-grant'`.
      const checksummedHolder = getAddress(address);
      // Snapshot the holder's existing 7715 grant contexts before we mint
      // a new one — so `onSuccess` can revoke the now-superseded ones
      // without ever touching the grant we're about to create.
      priorGrantsRef.current = await fetchPriorGrants(checksummedHolder);
      await enableVia7715({
        input,
        address: checksummedHolder,
        env,
        allowedUgms,
        protectedUgms,
        expiresAt,
        grantPermissions,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY(address) });
      // Best-effort: revoke the onchain grants the server just
      // superseded. Fire-and-forget — a revoke failure (or a wallet
      // without programmatic 7715 revoke) must never surface as an
      // enable failure.
      const prior = priorGrantsRef.current;
      priorGrantsRef.current = [];
      if (prior.length > 0 && address) {
        const holder = address;
        void (async () => {
          const r = await revokeContexts(prior.map((g) => g.permissionContext));
          // Stamp rows whose every context was revoked so they leave the
          // superseded count for good.
          await markGrantsClearedOnServer(holder, pickFullyRevokedIds(prior, r.revoked));
        })().catch(() => {});
      }
    },
  });

  const enable = useCallback(
    async (input: EnableInput) => {
      await mutation.mutateAsync(input);
    },
    [mutation],
  );

  return { enable, isPending: mutation.isPending, capability };
}

// ─── Enable helper ───────────────────────────────────────────────
//
// Pulled out of the mutation body to keep it readable. Requests the
// ERC-7715 grant from MetaMask, then POSTs the resulting
// `permissionContext` blobs to `/api/protection/enable`, which
// validates + persists the row stamped `signing_path = '7715-grant'`.

async function postEnable(body: unknown): Promise<void> {
  const res = await fetch('/api/protection/enable', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string })?.error ?? `Enable failed (${res.status})`);
  }
}

/**
 * Fetch the holder's existing 7715 grants (active + superseded) with
 * their delegation row ids, from the authenticated grants endpoint. Used
 * to capture the to-be-superseded grants before a re-enable so they can
 * be revoked + marked-cleared afterwards. Best-effort: any failure
 * returns `[]` so it can never block or fail the enable it's bolted onto.
 */
async function fetchPriorGrants(holder: Address): Promise<RevokableGrant[]> {
  try {
    const res = await fetch(`/api/protection/grants?address=${holder}`);
    if (!res.ok) return [];
    const json = (await res.json()) as {
      active?: Array<{ delegationId?: number; permissionContext?: string }>;
      superseded?: Array<{ delegationId?: number; permissionContext?: string }>;
    };
    return [...(json.active ?? []), ...(json.superseded ?? [])]
      .filter(
        (g): g is { delegationId: number; permissionContext: string } =>
          typeof g.delegationId === 'number' &&
          typeof g.permissionContext === 'string' &&
          g.permissionContext.startsWith('0x'),
      )
      .map((g) => ({ delegationId: g.delegationId, permissionContext: g.permissionContext as Hex }));
  } catch {
    return [];
  }
}

interface EnablePathArgs {
  input: EnableInput;
  address: Address;
  env: ProtectionEnvAddresses;
  allowedUgms: Address[];
  /**
   * Subset of `allowedUgms` (post-clamp) the holder ticked in the
   * picker. Passed through so the server persists the holder's intent
   * on the row — and so a future "rotate caps without re-picking
   * boards" UX can read the value back from it.
   */
  protectedUgms: Address[];
  expiresAt: number;
}

async function enableVia7715(
  args: EnablePathArgs & {
    grantPermissions: ReturnType<typeof useGrantProtectionPermissions>['grant'];
  },
): Promise<void> {
  const { input, address, env, allowedUgms, protectedUgms, expiresAt, grantPermissions } = args;
  if (!env.protectionRedeemerAddress) {
    // The 7715 grant REQUIRES a concrete delegate to grant permissions
    // TO. Without the redeemer deployed, we'd be granting to `0x0`,
    // which fails wallet-side. Throw early so the user sees a clean
    // toast rather than an opaque MetaMask rejection.
    throw new Error(
      'Land Protection is not yet configured for this wallet. ' +
        'The onchain redeemer is not deployed in this environment; ' +
        'please contact support.',
    );
  }

  // Pre-flight the server validation BEFORE requesting the irreversible
  // onchain grant. If the server would reject this configuration (a
  // V2.1 board on the MetaMask path, bad caps, auth not linked, chain
  // mismatch, missing env), surface it now — minting a grant we can't
  // persist orphans a MetaMask permission and leaves the toggle stuck
  // OFF. `dryRun` runs the exact same auth + validation the real POST
  // does, minus persistence.
  await postEnable({
    signingPath: '7715-grant',
    dryRun: true,
    holder: address,
    chainId: env.chainId,
    keeperAddress: env.protectionRedeemerAddress,
    weeklyUsdcCap: input.weeklyUsdcCap.toString(),
    weeklyTakeoverCap: input.weeklyTakeoverCap.toString(),
    allowedUgms,
    protectedUgms,
    runwayGateSeconds: input.runwayGateSeconds,
    targetRunwaySeconds: input.targetRunwaySeconds,
    expiresAt,
  });

  const grants = await grantPermissions({
    chainId: env.chainId,
    keeperAddress: env.protectionRedeemerAddress,
    expiresAt,
    weeklyUsdcCap: input.weeklyUsdcCap,
    weeklyTakeoverCap: input.weeklyTakeoverCap,
    usdcAddress: env.usdc,
    takeoverAddress: env.takeover,
  });
  if (grants.length === 0) {
    throw new Error('Wallet returned no permissions');
  }

  // The server stores one delegation per token grant. We submit them
  // as a batch so a partial-success state can't strand half the
  // user's caps without a corresponding row. `keeperAddress` in the
  // POST body retains its name for backward compatibility with the
  // server route's existing schema — it now carries the redeemer
  // contract address, which the route validates against the
  // `PROTECTION_REDEEMER_ADDRESS` env so a typo here can't smuggle in
  // a delegation pointing at the wrong contract.
  await postEnable({
    signingPath: '7715-grant',
    holder: address,
    chainId: env.chainId,
    keeperAddress: env.protectionRedeemerAddress,
    weeklyUsdcCap: input.weeklyUsdcCap.toString(),
    weeklyTakeoverCap: input.weeklyTakeoverCap.toString(),
    allowedUgms,
    protectedUgms,
    runwayGateSeconds: input.runwayGateSeconds,
    targetRunwaySeconds: input.targetRunwaySeconds,
    expiresAt,
    grants: grants.map((g) => ({
      permissionContext: g.permissionContext,
      delegationManager: g.delegationManager,
      tokenAddress: g.tokenAddress,
      periodAmount: g.periodAmount.toString(),
      periodDuration: g.periodDuration,
      expiresAt: g.expiresAt,
      dependencies: g.dependencies,
    })),
  });
}

function useStatusMutation(endpoint: 'disable' | 'revoke') {
  const { address } = useWallet();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input?: { txHash?: Hex }) => {
      if (!address) throw new Error('No connected wallet');
      const body: Record<string, unknown> = { holder: address };
      if (input?.txHash) body.txHash = input.txHash;
      const res = await fetch(`/api/protection/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string })?.error ?? `${endpoint} failed (${res.status})`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY(address) });
    },
  });
}

export function useDisableDepositProtection(): {
  disable: () => Promise<void>;
  isPending: boolean;
} {
  const m = useStatusMutation('disable');
  return { disable: async () => { await m.mutateAsync(undefined); }, isPending: m.isPending };
}

export function useRevokeDepositProtection(): {
  revoke: (input?: { txHash?: Hex }) => Promise<void>;
  isPending: boolean;
} {
  const m = useStatusMutation('revoke');
  return { revoke: async (input) => { await m.mutateAsync(input); }, isPending: m.isPending };
}
